import { Link, useRouterState } from '@tanstack/react-router'
import {
  BarChart3,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  Cpu,
  Download,
  Flag,
  Gift,
  Globe2,
  HandCoins,
  Handshake,
  LogOut,
  Megaphone,
  Menu,
  Shield,
  ShieldAlert,
  TrendingUp,
  UserPlus,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAdminAuth } from '~/entities/session/model/use-admin-auth'
import { useLogout } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { DURATION } from '~/shared/lib/motion'
import { storageManager } from '~/shared/lib/storage-manager'
import { cn } from '~/shared/lib/utils'
import { Avatar, AvatarFallback } from '~/shared/ui/avatar'
import { Button } from '~/shared/ui/button'
import { LanguageSwitcher } from '~/shared/ui/language-switcher'
import { Separator } from '~/shared/ui/separator'
import { ThemeToggle } from '~/shared/ui/theme-toggle'

const RAIL_COLLAPSED_KEY = 'pulsify.admin.rail.collapsed'

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    setCollapsed(storageManager.get<boolean>(RAIL_COLLAPSED_KEY) === true)
  }, [])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((value) => {
      const next = !value
      storageManager.set(RAIL_COLLAPSED_KEY, next)
      return next
    })
  }, [])

  const navigation = useMemo(
    () => [
      {
        name: m.statsTitle(),
        href: '/admin/stats',
        icon: BarChart3,
      },
      {
        name: m.aiCostsNav(),
        href: '/admin/ai-costs',
        icon: Cpu,
      },
      {
        name: m.businesses(),
        href: '/admin',
        icon: Building2,
      },
      {
        name: m.growthNav(),
        href: '/admin/map',
        icon: Globe2,
      },
      {
        name: m.opportunitiesNav(),
        href: '/admin/opportunities',
        icon: TrendingUp,
      },
      {
        name: m.signupsNav(),
        href: '/admin/signups',
        icon: UserPlus,
      },
      {
        name: m.downloadLinksNav(),
        href: '/admin/download-links',
        icon: Download,
      },
      {
        name: m.partnersNav(),
        href: '/admin/partners',
        icon: Handshake,
      },
      {
        name: m.shareReviewNav(),
        href: '/admin/share-cashback',
        icon: HandCoins,
      },
      {
        name: m.contentReports(),
        href: '/admin/content-reports',
        icon: Flag,
      },
      {
        name: m.moderationQueue(),
        href: '/admin/moderation',
        icon: ShieldAlert,
      },
      {
        name: m.drops(),
        href: '/admin/drops',
        icon: Gift,
      },
      {
        name: m.channelNav(),
        href: '/admin/channel',
        icon: Megaphone,
      },
    ],
    [],
  )

  const { admin } = useAdminAuth()
  const { mutate: logout, isPending } = useLogout({ redirectTo: '/admin-login' })
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  // The activity map can take over the background, and while it does the rail floats on top
  // of it instead of reserving a column beside it.
  const mapView = (routerState.location.search as { view?: string } | undefined)?.view
  const immersive = currentPath.startsWith('/admin/map') && mapView === 'expanded'

  const getInitials = (email?: string) => {
    if (!email) return 'A'
    return email.charAt(0).toUpperCase()
  }

  const closeSidebar = useCallback(() => setSidebarOpen(false), [])

  const sidebarContent = (
    <>
      <div
        className={cn(
          'flex items-center gap-3 border-border/60 border-b shrink-0',
          collapsed ? 'flex-col gap-2 p-3' : 'p-5',
        )}
      >
        <div className="flex justify-center items-center bg-brand rounded-lg w-10 h-10 shrink-0">
          <Shield className="w-5 h-5 text-brand-foreground" />
        </div>
        {collapsed ? null : (
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-foreground truncate">{m.adminPortal()}</h1>
            <p className="text-muted-foreground text-xs truncate">{m.systemManagement()}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleCollapsed}
          className="hidden lg:inline-flex shrink-0"
          aria-label={collapsed ? m.railExpand() : m.railCollapse()}
          title={collapsed ? m.railExpand() : m.railCollapse()}
        >
          {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
        </Button>
      </div>

      <nav className="flex-1 space-y-1 p-4">
        {navigation.map((item) => {
          const isActive =
            item.href === '/admin' ? currentPath === '/admin' : currentPath.startsWith(item.href)

          return (
            <Link
              key={item.name}
              to={item.href}
              title={collapsed ? item.name : undefined}
              className={cn(
                'flex items-center rounded-lg font-medium text-sm transition-colors',
                collapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2',
                isActive
                  ? 'bg-brand-subtle text-brand-subtle-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {collapsed ? null : <span className="truncate">{item.name}</span>}
            </Link>
          )
        })}
      </nav>

      <div className={cn('border-border/60 border-t shrink-0', collapsed ? 'p-3' : 'p-4')}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <Avatar className="border-border border w-9 h-9" title={admin?.email}>
              <AvatarFallback className="bg-brand font-medium text-brand-foreground text-sm">
                {getInitials(admin?.email)}
              </AvatarFallback>
            </Avatar>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => logout()}
              disabled={isPending}
              aria-label={m.signOut()}
              title={m.signOut()}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="border-border border w-10 h-10">
                <AvatarFallback className="bg-brand font-medium text-brand-foreground text-sm">
                  {getInitials(admin?.email)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-sm truncate">{m.admin()}</p>
                <p className="text-muted-foreground text-xs truncate">{admin?.email}</p>
              </div>
            </div>
            <Separator className="my-3" />
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeToggle />
              <Button
                variant="outline"
                size="sm"
                onClick={() => logout()}
                disabled={isPending}
                className="flex-1"
              >
                <LogOut className="mr-2 w-4 h-4" />
                <span>{isPending ? m.signingOut() : m.signOut()}</span>
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  )

  return (
    <div
      className="flex bg-background min-h-screen"
      style={{ '--admin-rail-inset': collapsed ? '6.5rem' : '18rem' } as React.CSSProperties}
    >
      <header className="fixed top-0 right-0 left-0 z-40 flex items-center gap-3 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 border-b h-14 lg:hidden">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
            {m.adminPortal()}
          </span>
        </div>
      </header>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay dismiss doesn't need keyboard */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: overlay dismiss pattern */}
          <div className="fixed inset-0 bg-black/50" onClick={closeSidebar} />
          <aside className="relative flex flex-col bg-white dark:bg-slate-900 w-64 h-full">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10"
              onClick={closeSidebar}
              aria-label={m.closeSidebar()}
            >
              <X className="w-4 h-4" />
            </Button>
            {sidebarContent}
          </aside>
        </div>
      )}

      <aside
        style={{ transitionDuration: `${DURATION.slow}ms` }}
        className={cn(
          'hidden lg:flex top-4 bottom-4 left-4 z-30 fixed flex-col bg-card/80 shadow-2xl backdrop-blur-xl border border-border/60 rounded-2xl overflow-hidden motion-safe:transition-[width] motion-safe:ease-out',
          collapsed ? 'w-[4.5rem]' : 'w-64',
        )}
      >
        {sidebarContent}
      </aside>

      <main
        style={{ transitionDuration: `${DURATION.slow}ms` }}
        className={cn(
          'flex-1 pt-14 lg:pt-0 overflow-auto motion-safe:transition-[margin] motion-safe:ease-out',
          immersive ? 'lg:ml-0' : collapsed ? 'lg:ml-[6rem]' : 'lg:ml-[17.5rem]',
        )}
      >
        {children}
      </main>
    </div>
  )
}
